import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Stack,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import { PendingChannel, OUTREACH_STATUS, WORKFLOW_STATUS } from '../../types/pendingChannels';
import { modalStyles } from '../../utils/modalStyles';
import { updatePendingChannel, approvePendingChannel } from '../../services/firestoreChannelService';
import { useAuth } from '../../contexts/AuthContext';

interface EditPendingChannelModalProps {
  open: boolean;
  channel: (PendingChannel & { _id: string }) | null;
  onClose: () => void;
  onSave: (channelId: string, updates: Partial<PendingChannel>) => Promise<void>;
  onApprove?: (channelId: string, updates: Partial<PendingChannel>) => Promise<void>;
  loading?: boolean;
  error?: string | null;
  targetTables?: string[];
}

interface EditFormState {
  // Contact Information
  email: string;
  instagram: string;
  twitter: string;
  website: string;
  
  // Outreach Status
  outreach_status: string;
  outreach_date: string;
  outreach_notes: string;
  
  // Approval Settings
  approval_target_table: string;
  notes: string;
}

const getDefaultFormState = (): EditFormState => ({
  email: '',
  instagram: '',
  twitter: '',
  website: '',
  outreach_status: OUTREACH_STATUS.PENDING,
  outreach_date: '',
  outreach_notes: '',
  approval_target_table: '',
  notes: '',
});

export const EditPendingChannelModal: React.FC<EditPendingChannelModalProps> = ({
  open,
  channel,
  onClose,
  onSave,
  onApprove,
  loading = false,
  error = null,
  targetTables = [],
}) => {
  const [formState, setFormState] = useState<EditFormState>(getDefaultFormState());
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const { currentUser } = useAuth();

  // Initialize form state when channel is selected
  useEffect(() => {
    if (channel && open) {
      setFormState({
        email: channel.email || '',
        instagram: channel.socialLinks?.instagram || '',
        twitter: channel.socialLinks?.twitter || '',
        website: channel.socialLinks?.website || channel.website || '',
        outreach_status: channel.outreach_status || OUTREACH_STATUS.PENDING,
        outreach_date: channel.outreach_date ? formatDateForInput(channel.outreach_date) : '',
        outreach_notes: channel.outreach_notes || '',
        approval_target_table: channel.approval_target_table || '',
        notes: channel.notes || '',
      });
    }
  }, [channel, open]);

  const handleFieldChange = (field: keyof EditFormState, value: string) => {
    setFormState((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    if (!channel || !currentUser) return;

    setIsLoading(true);
    setApiError(null);

    try {
      const updates: Partial<PendingChannel> = {
        email: formState.email.trim(),
        socialLinks: {
          twitter: formState.twitter.trim() || null,
          instagram: formState.instagram.trim() || null,
          facebook: null,
          website: formState.website.trim() || null,
        },
        website: formState.website.trim(),
        outreach_status: formState.outreach_status,
        outreach_notes: formState.outreach_notes.trim(),
        notes: formState.notes.trim(),
      };

      if (formState.outreach_date) {
        updates.outreach_date = new Date(formState.outreach_date) as any;
      }

      await updatePendingChannel(channel._id, updates, currentUser.email || currentUser.uid);
      
      // Call the onSave callback if provided
      try {
        await onSave(channel._id, updates);
      } catch (err) {
        console.error('Error in save callback:', err);
      }
      
      // Reset error and close modal
      setApiError(null);
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save changes';
      setApiError(errorMessage);
      console.error('Error saving channel:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAndApprove = async () => {
    if (!channel || !currentUser) return;

    // Validate required fields for approval
    if (!formState.email.trim()) {
      setApiError('Email is required for approval');
      return;
    }

    const hasContactMethod =
      formState.email.trim() ||
      formState.instagram.trim() ||
      formState.twitter.trim() ||
      formState.website.trim();

    if (!hasContactMethod) {
      setApiError('At least one contact method is required for approval');
      return;
    }

    if (!formState.approval_target_table) {
      setApiError('Target table is required for approval');
      return;
    }

    setIsLoading(true);
    setApiError(null);

    try {
      // First, update the channel with contact and outreach info
      const updates: Partial<PendingChannel> = {
        email: formState.email.trim(),
        socialLinks: {
          twitter: formState.twitter.trim() || null,
          instagram: formState.instagram.trim() || null,
          facebook: null,
          website: formState.website.trim() || null,
        },
        website: formState.website.trim(),
        outreach_status: formState.outreach_status,
        outreach_notes: formState.outreach_notes.trim(),
        notes: formState.notes.trim(),
      };

      if (formState.outreach_date) {
        updates.outreach_date = new Date(formState.outreach_date) as any;
      }

      // Update first to save contact/outreach info
      await updatePendingChannel(channel._id, updates, currentUser.email || currentUser.uid);

      // Then approve the channel
      await approvePendingChannel(
        channel._id,
        formState.approval_target_table,
        currentUser.email || currentUser.uid
      );

      setApiError(null);
      onClose();

      // Call the optional approval callback if provided
      if (onApprove) {
        try {
          await onApprove(channel._id, { ...updates, workflow_status: WORKFLOW_STATUS.APPROVED });
        } catch (err) {
          console.error('Error in approval callback:', err);
        }
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to approve channel';
      setApiError(errorMessage);
      console.error('Error approving channel:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Validation
  const emailValid = !formState.email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formState.email);
  const hasContactMethod =
    formState.email.trim() ||
    formState.instagram.trim() ||
    formState.twitter.trim() ||
    formState.website.trim();
  const canApprove = emailValid && formState.email.trim() && hasContactMethod && formState.approval_target_table;

  if (!channel) {
    return null;
  }

  return (
    <Dialog 
      open={open} 
      onClose={isLoading ? undefined : onClose} 
      maxWidth="md" 
      fullWidth
      sx={modalStyles.dialog}
    >
      <DialogTitle sx={modalStyles.dialogTitle}>
        Edit Pending Channel: {channel.title}
      </DialogTitle>
      
      <DialogContent sx={modalStyles.dialogContent}>
        <Stack spacing={3}>
          {/* Channel Information Display Section */}
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Channel Information
            </Typography>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
                <Avatar
                  src={`https://www.youtube.com/favicon.ico`}
                  alt={channel.title}
                  sx={{ width: 64, height: 64 }}
                />
                <Stack spacing={1} flex={1}>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      Channel Name
                    </Typography>
                    <Typography variant="body1">{channel.title}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">
                      URL
                    </Typography>
                    <Typography 
                      component="a" 
                      href={channel.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {channel.url}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>

              <Box>
                <Typography variant="subtitle2" color="textSecondary">
                  Description
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {channel.description || '-'}
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Subscribers
                  </Typography>
                  <Typography variant="body2">
                    {channel.subscriber_count?.toLocaleString() || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Videos
                  </Typography>
                  <Typography variant="body2">
                    {channel.video_count?.toLocaleString() || '-'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">
                    Total Views
                  </Typography>
                  <Typography variant="body2">
                    {channel.view_count?.toLocaleString() || '-'}
                  </Typography>
                </Box>
              </Stack>


            </Stack>
          </Paper>

          {/* Contact Information Form Section */}
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Contact Information
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Email"
                type="email"
                value={formState.email}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                fullWidth
                required
                error={!emailValid}
                helperText={!emailValid ? 'Invalid email format' : ''}
                disabled={isLoading}
              />
              <TextField
                label="Instagram"
                value={formState.instagram}
                onChange={(e) => handleFieldChange('instagram', e.target.value)}
                fullWidth
                placeholder="e.g., @handle or https://instagram.com/handle"
                disabled={isLoading}
              />
              <TextField
                label="Twitter/X"
                value={formState.twitter}
                onChange={(e) => handleFieldChange('twitter', e.target.value)}
                fullWidth
                placeholder="e.g., @handle or https://twitter.com/handle"
                disabled={isLoading}
              />
              <TextField
                label="Website"
                value={formState.website}
                onChange={(e) => handleFieldChange('website', e.target.value)}
                fullWidth
                placeholder="e.g., https://example.com"
                disabled={isLoading}
              />
              {!hasContactMethod && (
                <Alert severity="warning">
                  At least one contact method (email, Instagram, Twitter, or website) is required.
                </Alert>
              )}
            </Stack>
          </Paper>

          {/* Outreach Status Section */}
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Outreach Status
            </Typography>
            <Stack spacing={2}>
              <FormControl fullWidth disabled={isLoading}>
                <InputLabel>Outreach Status</InputLabel>
                <Select
                  value={formState.outreach_status}
                  onChange={(e) => handleFieldChange('outreach_status', e.target.value)}
                  label="Outreach Status"
                >
                  <MenuItem value={OUTREACH_STATUS.PENDING}>Pending</MenuItem>
                  <MenuItem value={OUTREACH_STATUS.CONTACTED}>Contacted</MenuItem>
                  <MenuItem value={OUTREACH_STATUS.RESPONDED}>Responded</MenuItem>
                  <MenuItem value={OUTREACH_STATUS.NOT_INTERESTED}>Not Interested</MenuItem>
                  <MenuItem value={OUTREACH_STATUS.CONVERTED}>Converted</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Outreach Date"
                type="date"
                value={formState.outreach_date}
                onChange={(e) => handleFieldChange('outreach_date', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                disabled={isLoading}
              />
              <TextField
                label="Outreach Notes"
                value={formState.outreach_notes}
                onChange={(e) => handleFieldChange('outreach_notes', e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="Add notes about outreach attempts, responses, or communications..."
                disabled={isLoading}
              />
            </Stack>
          </Paper>

          {/* Approval Settings Section */}
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Approval Settings
            </Typography>
            <Stack spacing={2}>
              <FormControl fullWidth disabled={isLoading}>
                <InputLabel>Target Table</InputLabel>
                <Select
                  value={formState.approval_target_table}
                  onChange={(e) => handleFieldChange('approval_target_table', e.target.value)}
                  label="Target Table"
                >
                  <MenuItem value="">-- Select Target Table --</MenuItem>
                  {targetTables.map((table) => (
                    <MenuItem key={table} value={table}>
                      {table}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Internal Notes"
                value={formState.notes}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="Add internal notes for approval..."
                disabled={isLoading}
              />
            </Stack>
          </Paper>

          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={modalStyles.dialogActions}>
        <Button 
          onClick={onClose} 
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSave}
          disabled={loading || !emailValid}
          variant="outlined"
        >
          {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          Save Changes
        </Button>
        <Button 
          onClick={handleSaveAndApprove}
          disabled={!canApprove || loading}
          variant="contained"
          color="success"
        >
          {loading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
          Save & Approve
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Helper function to format date for input
function formatDateForInput(date: any): string {
  if (!date) return '';
  try {
    const d = date instanceof Date ? date : new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
}

export default EditPendingChannelModal;
